import { useAppDispatch, useAppSelector } from '@app/core/hooks/redux-hooks';
import { MinorityDiscountsDto } from '@app/shared/models/contracts/project-dto';
import { FC, Fragment } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as projectActions from '@core/store/project-slice';
import { percentageFieldFormattingProps } from '@app/shared/components/form-controls/form-field/form-field-patterns';
import { RouteConstants } from '@app/modules/projects/RouteConstants';
import { cloneToMutableData } from '@app/shared/helpers';
import { useLocale } from '@app/core/hooks/useLocale';
import useSetSelectedTab from '@app/core/hooks/useSetSelectedTab';
import { setValuationInputsSelectedTab } from '@app/core/store/ui-values-slice';
import { selectInstruments } from '@app/core/store/capital-structure-slice-selectors';
import useInstrumentSort from '@app/shared/hooks/useInstrumentSort';
import {
  TABLE_COL_WIDTH_ALTERNATIVE,
  TABLE_LABELS_COL_WIDTH_ALTERNATIVE,
} from '@app/shared/constants/table-config';
import GridTextField from '@app/shared/components/grid-controls/grid-text-field/GridTextField';
import useTriggerEffect from '@app/core/hooks/useTriggerEffect';
import { minMaxValidator } from '@app/core/validations/hook-forms/validators';
import { formConfigBase } from '@app/shared/constants/form-config-base';

const DLOCTable: FC = (): JSX.Element => {
  const { l } = useLocale();
  useSetSelectedTab(RouteConstants.DLOC, setValuationInputsSelectedTab);

  const dispatch = useAppDispatch();
  const projectDraft = useAppSelector((state) => state.project.projectDraft);
  const { minorityDiscounts } = projectDraft.valuationInputs;

  const instruments = useAppSelector(selectInstruments);
  const valuedInstruments = instruments.filter((instrument) => instrument.shouldBeValued);
  const sortedValuedInstrument = useInstrumentSort(valuedInstruments);

  const formMethods = useForm<Partial<MinorityDiscountsDto>>({
    ...formConfigBase,
    defaultValues: {
      controlPremiumForDloc: cloneToMutableData(minorityDiscounts.controlPremiumForDloc),
    },
  });
  const { trigger, getValues } = formMethods;

  useTriggerEffect(trigger);
  const formSubmitHandler = () => {
    const formValues = getValues();

    const updatedProjectDraft = {
      ...projectDraft,
      valuationInputs: {
        ...projectDraft.valuationInputs,
        minorityDiscounts: {
          ...minorityDiscounts,
          controlPremiumForDloc: cloneToMutableData(formValues.controlPremiumForDloc ?? {}),
        },
      },
    };

    dispatch(projectActions.updateProjectDraft({ project: updatedProjectDraft }));
  };

  return (
    <FormProvider {...formMethods}>
      <form onBlur={formMethods.handleSubmit(formSubmitHandler, formSubmitHandler)}>
        <table
          className="table-primary table-primary--zebra table-primary--fixed table-primary"
          style={{
            width: TABLE_COL_WIDTH_ALTERNATIVE + TABLE_LABELS_COL_WIDTH_ALTERNATIVE + 'px',
          }}>
          <colgroup>
            <col style={{ width: `${TABLE_LABELS_COL_WIDTH_ALTERNATIVE}px` }} />
            <col style={{ width: `${TABLE_COL_WIDTH_ALTERNATIVE}px` }} />
          </colgroup>
          <thead className="table-primary__sticky-section table-primary__sticky-section--table-single-row-header">
            <tr>
              <th className="table-primary__cell--header" colSpan={2}>
                {l('_ControlPremiumForDloc')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedValuedInstrument.map((instrument, instrumentIndex) => (
              <Fragment key={`instrument-row-fragment-${instrument.instrumentId}`}>
                <tr
                  key={'instrument-row-wrapper' + instrument.instrumentId}
                  data-testid={`dloc-instrument-row-${instrument.instrumentId}`}>
                  <td className="table-primary__cell--vertical-separator">
                    {instrument.instrumentNarrative}
                  </td>
                  <td className="table-primary__cell--vertical-separator">
                    <GridTextField
                      name={`controlPremiumForDloc.${instrument.instrumentId}`}
                      {...percentageFieldFormattingProps}
                      placeholder="0.00%"
                      rules={{ ...minMaxValidator(0, 100) }}
                    />
                  </td>
                </tr>
                {instrumentIndex < sortedValuedInstrument.length - 1 && (
                  <tr key={'instrument-spacer-row' + instrument.instrumentId}>
                    <td className="table-primary__cell--vertical-separator" />
                    <td className="table-primary__cell--vertical-separator" />
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </form>
    </FormProvider>
  );
};

export default DLOCTable;
